import {
  STORAGE_KEY,
  STORAGE_KEY_THEME,
  STORAGE_KEY_BASEMAP,
  STORAGE_KEY_LAYER,
  STORAGE_KEY_POLYGON_OPACITY,
  STORAGE_KEY_SEARCH_TYPE,
  STORAGE_KEY_QUERY_ATTRIBUTE,
  STORAGE_KEY_QUERY_VALUE,
  STORAGE_KEY_QUERY_STATE,
  STORAGE_KEY_LLD_SECTION,
  STORAGE_KEY_LLD_TOWNSHIP,
  STORAGE_KEY_LLD_RANGE,
  STORAGE_KEY_LLD_MERIDIAN,
  STORAGE_KEY_LATITUDE,
  STORAGE_KEY_LONGITUDE,
  STORAGE_KEY_UTM_ZONE,
  STORAGE_KEY_UTM_EASTING,
  STORAGE_KEY_UTM_NORTHING,
  STORAGE_KEY_RM_SELECTED,
  MAP_ZOOM_LEVEL,
  MAP_THEME,
  MAP_BASEMAP,
  MAP_LAYER,
  MAP_POLYGON_OPACITY,
  DEFAULT_SEARCH_TYPE,
} from '@/lib/constants';
import { QueryCondition } from '@/lib/types';

export interface MapSettings {
  zoomLevel: number;
  theme: string;
  basemap: string;
  layers: string[];
  polygonOpacity: number;
  searchType: string;
}

export class StorageManager {
  private static instance: StorageManager;

  private constructor() {}

  public static getInstance(): StorageManager {
    if (!StorageManager.instance) {
      StorageManager.instance = new StorageManager();
    }
    return StorageManager.instance;
  }

  // Save individual settings
  public saveZoomLevel(zoomLevel: number): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, zoomLevel.toString());
    }
  }

  public saveTheme(theme: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_THEME, theme);
    }
  }

  public saveBasemap(basemap: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_BASEMAP, basemap);
    }
  }

  public saveLayers(layers: string[]): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_LAYER, layers.join(','));
    }
  }

  public savePolygonOpacity(opacity: number): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_POLYGON_OPACITY, opacity.toString());
    }
  }

  public saveSearchType(searchType: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_SEARCH_TYPE, searchType);
    }
  }

  public saveQueryAttribute(attribute: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_QUERY_ATTRIBUTE, attribute);
    }
  }

  public saveQueryValue(value: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_QUERY_VALUE, value);
    }
  }

  public saveQueryState(state: QueryCondition[]): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_QUERY_STATE, JSON.stringify(state));
    }
  }

  public clearQueryState(): void {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY_QUERY_STATE);
    }
  }

  // LLD Search methods
  public saveLLDSection(section: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_LLD_SECTION, section);
    }
  }

  public saveLLDTownship(township: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_LLD_TOWNSHIP, township);
    }
  }

  public saveLLDRange(range: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_LLD_RANGE, range);
    }
  }

  public saveLLDMeridian(meridian: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_LLD_MERIDIAN, meridian);
    }
  }

  public saveLLDState(section: string, township: string, range: string, meridian: string): void {
    this.saveLLDSection(section);
    this.saveLLDTownship(township);
    this.saveLLDRange(range);
    this.saveLLDMeridian(meridian);
  }

  // Lat-Long Search methods
  public saveLatitude(latitude: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_LATITUDE, latitude);
    }
  }

  public saveLongitude(longitude: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_LONGITUDE, longitude);
    }
  }

  public saveLatLongState(latitude: string, longitude: string): void {
    this.saveLatitude(latitude);
    this.saveLongitude(longitude);
  }

  // UTM Search methods
  public saveUTMZone(zone: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_UTM_ZONE, zone);
    }
  }

  public saveUTMEasting(easting: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_UTM_EASTING, easting);
    }
  }

  public saveUTMNorthing(northing: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_UTM_NORTHING, northing);
    }
  }

  public saveUTMState(zone: string, easting: string, northing: string): void {
    this.saveUTMZone(zone);
    this.saveUTMEasting(easting);
    this.saveUTMNorthing(northing);
  }

  // RM Search methods
  public saveRMSelected(selectedRm: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_RM_SELECTED, selectedRm);
    }
  }

  // Load individual settings
  public loadZoomLevel(): number {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? parseInt(stored, 10) : MAP_ZOOM_LEVEL;
    }
    return MAP_ZOOM_LEVEL;
  }

  public loadTheme(): string {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY_THEME);
      return stored || MAP_THEME;
    }
    return MAP_THEME;
  }

  public loadBasemap(): string {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY_BASEMAP);
      return stored || MAP_BASEMAP;
    }
    return MAP_BASEMAP;
  }

  public loadLayers(): string[] {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY_LAYER);
      return stored ? stored.split(',') : MAP_LAYER;
    }
    return MAP_LAYER;
  }

  public loadPolygonOpacity(): number {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY_POLYGON_OPACITY);
      return stored ? parseInt(stored) : MAP_POLYGON_OPACITY;
    }
    return MAP_POLYGON_OPACITY;
  }

  public loadSearchType(): string {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY_SEARCH_TYPE);
      return stored || DEFAULT_SEARCH_TYPE;
    }
    return DEFAULT_SEARCH_TYPE;
  }

  public loadQueryState(): QueryCondition[] {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY_QUERY_STATE);
      if (!stored) return [];
      try {
        const parsed = JSON.parse(stored);
        // Back-compat: old object format { conditions, isActive }
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && Array.isArray(parsed.conditions)) {
          return parsed.conditions as QueryCondition[];
        }
        if (Array.isArray(parsed)) {
          return parsed as QueryCondition[];
        }
        return [];
      } catch {
        return [];
      }
    }
    return [];
  }

  // LLD Search load methods
  public loadLLDSection(): string {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY_LLD_SECTION) || '';
    }
    return '';
  }

  public loadLLDTownship(): string {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY_LLD_TOWNSHIP) || '';
    }
    return '';
  }

  public loadLLDRange(): string {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY_LLD_RANGE) || '';
    }
    return '';
  }

  public loadLLDMeridian(): string {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY_LLD_MERIDIAN) || '';
    }
    return '';
  }

  public loadLLDState(): { section: string; township: string; range: string; meridian: string } {
    return {
      section: this.loadLLDSection(),
      township: this.loadLLDTownship(),
      range: this.loadLLDRange(),
      meridian: this.loadLLDMeridian(),
    };
  }

  // Lat-Long Search load methods
  public loadLatitude(): string {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY_LATITUDE) || '';
    }
    return '';
  }

  public loadLongitude(): string {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY_LONGITUDE) || '';
    }
    return '';
  }

  public loadLatLongState(): { latitude: string; longitude: string } {
    return {
      latitude: this.loadLatitude(),
      longitude: this.loadLongitude(),
    };
  }

  // UTM Search load methods
  public loadUTMZone(): string {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY_UTM_ZONE);
      return stored || '';
    }
    return '';
  }

  public loadUTMEasting(): string {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY_UTM_EASTING);
      return stored || '';
    }
    return '';
  }

  public loadUTMNorthing(): string {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY_UTM_NORTHING);
      return stored || '';
    }
    return '';
  }

  public loadUTMState(): { zone: string; easting: string; northing: string } {
    return {
      zone: this.loadUTMZone(),
      easting: this.loadUTMEasting(),
      northing: this.loadUTMNorthing(),
    };
  }

  // RM Search load methods
  public loadRMSelected(): string {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY_RM_SELECTED) || '';
    }
    return '';
  }

  // Save all settings at once
  public saveAllSettings(settings: MapSettings): void {
    this.saveZoomLevel(settings.zoomLevel);
    this.saveTheme(settings.theme);
    this.saveBasemap(settings.basemap);
    this.saveLayers(settings.layers);
    this.savePolygonOpacity(settings.polygonOpacity);
    this.saveSearchType(settings.searchType);
  }

  // Load all settings at once
  public loadAllSettings(): MapSettings {
    return {
      zoomLevel: this.loadZoomLevel(),
      theme: this.loadTheme(),
      basemap: this.loadBasemap(),
      layers: this.loadLayers(),
      polygonOpacity: this.loadPolygonOpacity(),
      searchType: this.loadSearchType(),
    };
  }

  // Clear all settings
  public clearAllSettings(): void {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_KEY_THEME);
      localStorage.removeItem(STORAGE_KEY_BASEMAP);
      localStorage.removeItem(STORAGE_KEY_LAYER);
      localStorage.removeItem(STORAGE_KEY_POLYGON_OPACITY);
      localStorage.removeItem(STORAGE_KEY_SEARCH_TYPE);
      localStorage.removeItem(STORAGE_KEY_QUERY_ATTRIBUTE);
      localStorage.removeItem(STORAGE_KEY_QUERY_VALUE);
      localStorage.removeItem(STORAGE_KEY_QUERY_STATE);
      localStorage.removeItem(STORAGE_KEY_LLD_SECTION);
      localStorage.removeItem(STORAGE_KEY_LLD_TOWNSHIP);
      localStorage.removeItem(STORAGE_KEY_LLD_RANGE);
      localStorage.removeItem(STORAGE_KEY_LLD_MERIDIAN);
      localStorage.removeItem(STORAGE_KEY_LATITUDE);
      localStorage.removeItem(STORAGE_KEY_LONGITUDE);
    }
  }

  // Reset to default settings
  public resetToDefaults(): void {
    this.saveAllSettings({
      zoomLevel: MAP_ZOOM_LEVEL,
      theme: MAP_THEME,
      basemap: MAP_BASEMAP,
      layers: MAP_LAYER,
      polygonOpacity: MAP_POLYGON_OPACITY,
      searchType: DEFAULT_SEARCH_TYPE,
    });
  }
}

// Export a singleton instance
export const storageManager = StorageManager.getInstance(); 